import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Calculator, Sigma } from "lucide-react";
import MathContent from "../components/MathContent";

/**
 * Khan-style Math Reference Sheet — covers Algebra, Exponents, Radicals, Logs,
 * Quadratic, Complex Numbers, Factoring, Absolute Value, and Inequalities.
 *
 * Each entry uses LaTeX rendered via KaTeX through <MathContent />.
 */

const Section = ({ title, icon: Icon, color, children, testId }) => (
  <div className="card-pop" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: "#1E3A5F" }}>
      {Icon && <Icon className="w-5 h-5" style={{ color }} />}
      <h2 className="font-heading font-bold text-base uppercase tracking-wide" style={{ color }}>
        {title}
      </h2>
    </div>
    <div className="space-y-2.5 text-sm">{children}</div>
  </div>
);

const Item = ({ label, math }) => (
  <div className="flex items-baseline gap-2">
    {label && (
      <span className="text-xs font-bold uppercase text-[#94A3B8] flex-shrink-0 min-w-[100px]">
        {label}
      </span>
    )}
    <div className="text-white">
      <MathContent text={math} />
    </div>
  </div>
);

const MathReference = () => {
  return (
    <div className="space-y-6" data-testid="math-reference-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/lessons" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white text-sm mb-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Lessons
          </Link>
          <h1 className="font-heading font-black text-2xl lg:text-3xl text-white">
            Math Reference Sheet
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1">
            All the formulas you need — ready before, during, and after every lesson.
          </p>
        </div>
        <div className="hidden lg:flex w-14 h-14 rounded-2xl bg-[#00D46A] items-center justify-center">
          <Calculator className="w-7 h-7 text-[#0A1628]" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* ARITHMETIC PROPERTIES */}
        <Section title="Arithmetic Properties" icon={Sigma} color="#00D46A" testId="ref-arithmetic">
          <Item label="Associative" math="$a(bc) = (ab)c$" />
          <Item label="Commutative" math="$a + b = b + a$ and $ab = ba$" />
          <Item label="Distributive" math="$a(b + c) = ab + ac$" />
        </Section>

        {/* EXPONENT PROPERTIES */}
        <Section title="Exponent Properties" icon={Sigma} color="#00FF7F" testId="ref-exponents">
          <Item math="$a^n a^m = a^{n+m}$" />
          <Item math="$(a^n)^m = a^{nm}$" />
          <Item math="$(ab)^n = a^n b^n$" />
          <Item math="$a^{-n} = \dfrac{1}{a^n}$" />
          <Item math="$\left(\dfrac{a}{b}\right)^{-n} = \left(\dfrac{b}{a}\right)^n = \dfrac{b^n}{a^n}$" />
          <Item math="$\dfrac{a^n}{a^m} = a^{n-m} = \dfrac{1}{a^{m-n}}$" />
          <Item math="$a^0 = 1, \; a \neq 0$" />
          <Item math="$\left(\dfrac{a}{b}\right)^n = \dfrac{a^n}{b^n}$" />
          <Item math="$\dfrac{1}{a^{-n}} = a^n$" />
          <Item math="$a^{n/m} = \left(a^{1/m}\right)^n = (a^n)^{1/m}$" />
        </Section>

        {/* INEQUALITIES */}
        <Section title="Properties of Inequalities" icon={Sigma} color="#7dd3fc" testId="ref-inequalities">
          <Item math="If $a < b$ then $a + c < b + c$ and $a - c < b - c$" />
          <Item math="If $a < b$ and $c > 0$ then $ac < bc$ and $a/c < b/c$" />
          <Item math="If $a < b$ and $c < 0$ then $ac > bc$ and $a/c > b/c$" />
        </Section>

        {/* QUADRATIC */}
        <Section title="Quadratic Equation" icon={Sigma} color="#fcd34d" testId="ref-quadratic">
          <Item math="For the equation $ax^2 + bx + c = 0$:" />
          <Item math="$$x = \dfrac{-b \pm \sqrt{b^2 - 4ac}}{2a}$$" />
        </Section>

        {/* RADICAL */}
        <Section title="Radical Properties" icon={Sigma} color="#00D46A" testId="ref-radicals">
          <Item math="$a, b \geq 0$ for even $n$" />
          <Item math="$\sqrt[n]{a} = a^{1/n}$" />
          <Item math="$\sqrt[m]{\sqrt[n]{a}} = \sqrt[mn]{a}$" />
          <Item math="$\sqrt[n]{ab} = \sqrt[n]{a}\,\sqrt[n]{b}$" />
          <Item math="$\sqrt[n]{\dfrac{a}{b}} = \dfrac{\sqrt[n]{a}}{\sqrt[n]{b}}$" />
          <Item math="$\sqrt[n]{a^n} = a$, if $n$ is odd" />
          <Item math="$\sqrt[n]{a^n} = |a|$, if $n$ is even" />
        </Section>

        {/* LOG */}
        <Section title="Logarithm Properties" icon={Sigma} color="#00FF7F" testId="ref-logs">
          <Item math="If $y = \log_b x$ then $b^y = x$" />
          <Item math="$\log_b b = 1$ and $\log_b 1 = 0$" />
          <Item math="$\log_b b^x = x$" />
          <Item math="$b^{\log_b x} = x$" />
          <Item math="$\log_a x = \dfrac{\log_b x}{\log_b a}$" />
          <Item math="$\log_b(x^r) = r \log_b x$" />
          <Item math="$\log_b(xy) = \log_b x + \log_b y$" />
          <Item math="$\log_b\!\left(\dfrac{x}{y}\right) = \log_b x - \log_b y$" />
        </Section>

        {/* COMPLEX NUMBERS */}
        <Section title="Complex Numbers" icon={Sigma} color="#d8b4fe" testId="ref-complex">
          <Item math="$i = \sqrt{-1}, \quad i^2 = -1$" />
          <Item math="$\sqrt{-a} = i\sqrt{a}, \; a \geq 0$" />
          <Item math="$(a + bi) + (c + di) = (a + c) + (b + d)i$" />
          <Item math="$(a + bi) - (c + di) = (a - c) + (b - d)i$" />
          <Item math="$(a + bi)(c + di) = (ac - bd) + (ad + bc)i$" />
          <Item math="$(a + bi)(a - bi) = a^2 + b^2$" />
          <Item math="$|a + bi| = \sqrt{a^2 + b^2}$" />
          <Item math="$\overline{a + bi} = a - bi$" />
        </Section>

        {/* FACTORING */}
        <Section title="Common Factoring" icon={Sigma} color="#fdba74" testId="ref-factoring">
          <Item math="$x^2 - a^2 = (x + a)(x - a)$" />
          <Item math="$x^2 + 2ax + a^2 = (x + a)^2$" />
          <Item math="$x^2 - 2ax + a^2 = (x - a)^2$" />
          <Item math="$x^2 + (a + b)x + ab = (x + a)(x + b)$" />
          <Item math="$x^3 + 3ax^2 + 3a^2 x + a^3 = (x + a)^3$" />
          <Item math="$x^3 + a^3 = (x + a)(x^2 - ax + a^2)$" />
          <Item math="$x^3 - a^3 = (x - a)(x^2 + ax + a^2)$" />
          <Item math="$x^{2n} - a^{2n} = (x^n - a^n)(x^n + a^n)$" />
        </Section>

        {/* ABSOLUTE VALUE */}
        <Section title="Absolute Value" icon={Sigma} color="#f9a8d4" testId="ref-absolute">
          <Item math="$|a| = \begin{cases} a, & \text{if } a \geq 0 \\ -a, & \text{if } a < 0 \end{cases}$" />
          <Item math="$|a| = |-a|$" />
          <Item math="$|a| \geq 0$" />
          <Item math="$|ab| = |a|\,|b|$" />
          <Item math="$\left|\dfrac{a}{b}\right| = \dfrac{|a|}{|b|}$" />
          <Item math="$|a + b| \leq |a| + |b|$" />
        </Section>

        {/* ARITHMETIC OPERATIONS */}
        <Section title="Arithmetic Operations" icon={Sigma} color="#00D46A" testId="ref-operations">
          <Item math="$ab + ac = a(b + c)$" />
          <Item math="$\dfrac{a}{b} - \dfrac{c}{d} = \dfrac{ad - bc}{bd}$" />
          <Item math="$a\!\left(\dfrac{b}{c}\right) = \dfrac{ab}{c}$" />
          <Item math="$\dfrac{a-b}{c-d} = \dfrac{b-a}{d-c}$" />
          <Item math="$\dfrac{a+b}{c} = \dfrac{a}{c} + \dfrac{b}{c}$" />
          <Item math="$\dfrac{ab + ac}{a} = b + c, \; a \neq 0$" />
          <Item math="$\dfrac{a}{b} + \dfrac{c}{d} = \dfrac{ad + bc}{bd}$" />
        </Section>
      </div>

      {/* CTA back to lessons */}
      <div className="card-pop text-center">
        <BookOpen className="w-10 h-10 mx-auto mb-2 text-[#00D46A]" />
        <h3 className="font-heading font-bold text-lg text-white mb-1">Ready to apply this?</h3>
        <p className="text-[#94A3B8] text-sm mb-4">
          Start an interactive lesson and put these formulas to work.
        </p>
        <Link
          to="/lessons"
          className="btn-primary inline-block"
          data-testid="ref-to-lessons"
        >
          Go to Lessons
        </Link>
      </div>
    </div>
  );
};

export default MathReference;
